import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiKey, FiLock, FiMail, FiRefreshCw, FiShield, FiUser } from 'react-icons/fi';
import api from '../api';
import PublicLayout from '../components/PublicLayout';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

function getAuthFeedback(error, fallback) {
  const data = error?.response?.data ?? {};
  const message =
    typeof data.message === 'string' && data.message.trim()
      ? data.message.trim()
      : fallback;

  return {
    accountRemoved: data.accountRemoved === true,
    allowImmediateResend: data.allowImmediateResend === true,
    message,
    registrationPending: data.registrationPending === true,
    recoveryAction:
      typeof data.recoveryAction === 'string' ? data.recoveryAction.trim() : '',
    status: error?.response?.status,
  };
}

function shouldRestartRegistration(feedback) {
  return feedback.recoveryAction === 'restart_registration' || feedback.accountRemoved || feedback.status === 404;
}

function shouldRetryRegistration(feedback) {
  return feedback.recoveryAction === 'retry_registration' || feedback.accountRemoved;
}

function shouldContinueVerification(feedback) {
  return feedback.recoveryAction === 'complete_registration' || feedback.registrationPending;
}

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [otp, setOtp] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('register');

  useEffect(() => {
    const routeState = location.state;
    if (!routeState || typeof routeState !== 'object') return;

    const initialEmail =
      typeof routeState.initialForm?.email === 'string'
        ? routeState.initialForm.email.trim()
        : '';
    const nextPendingEmail =
      typeof routeState.pendingEmail === 'string'
        ? routeState.pendingEmail.trim()
        : '';
    const nextMessage =
      typeof routeState.infoMessage === 'string'
        ? routeState.infoMessage.trim()
        : '';
    const nextStep =
      routeState.initialStep === 'verify' && nextPendingEmail
        ? 'verify'
        : 'register';

    if (initialEmail) {
      setForm((current) => ({ ...current, email: initialEmail }));
    }
    if (nextPendingEmail) {
      setPendingEmail(nextPendingEmail);
    }
    if (nextMessage) {
      setMessage(nextMessage);
    }
    if (nextStep === 'verify') {
      setStep('verify');
    }
  }, [location.state]);

  const updateField = (name) => (event) => {
    setForm((current) => ({ ...current, [name]: event.target.value }));
  };

  const resetToRegisterStep = (nextMessage) => {
    setStep('register');
    setPendingEmail('');
    setOtp('');
    setError('');
    setMessage(
      nextMessage ||
        'We reset the unfinished signup. Register again to receive a fresh verification code.',
    );
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await api.post('/users/register', form);
      setPendingEmail(form.email.trim());
      setStep('verify');
      setMessage('We sent a verification code to your email.');
    } catch (err) {
      const feedback = getAuthFeedback(err, 'Registration failed.');
      if (shouldContinueVerification(feedback)) {
        setPendingEmail(form.email.trim());
        setStep('verify');
        setMessage(feedback.message);
        return;
      }
      if (shouldRetryRegistration(feedback)) {
        setMessage(feedback.message);
        return;
      }
      setError(feedback.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const res = await api.post('/users/verify-register-otp', {
        email: pendingEmail,
        otp,
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/dashboard');
    } catch (err) {
      const feedback = getAuthFeedback(err, 'Verification failed.');
      if (shouldContinueVerification(feedback)) {
        setMessage(feedback.message);
        return;
      }
      if (shouldRestartRegistration(feedback)) {
        resetToRegisterStep(feedback.message);
        return;
      }
      setError(feedback.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const res = await api.post('/users/resend-register-otp', { email: pendingEmail });
      setMessage(res.data?.message || 'A new verification code was sent.');
    } catch (err) {
      const feedback = getAuthFeedback(err, 'Could not resend verification code.');
      if (shouldContinueVerification(feedback)) {
        setMessage(feedback.message);
        return;
      }
      if (shouldRestartRegistration(feedback)) {
        resetToRegisterStep(feedback.message);
        return;
      }
      setError(feedback.message);
    } finally {
      setLoading(false);
    }
  };

  const maskedEmail = pendingEmail
    ? pendingEmail.replace(/(^.).*(@.*$)/, '$1***$2')
    : '';

  return (
    <PublicLayout>
      <div className="px-4 py-8 sm:px-6 sm:py-14">
        <div className="mx-auto grid min-h-[calc(100vh-13rem)] max-w-6xl gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-[2.4rem] border border-[#ffcc00]/12 bg-[linear-gradient(180deg,#1c1b1b,#131313)] p-8 shadow-[0_24px_90px_rgba(0,0,0,0.25)] backdrop-blur-xl lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#ffe8a8]">Shimbirr signup</p>
            <h1 className="mt-4 text-4xl font-black tracking-[-0.04em] text-white sm:text-5xl">
              Create your Shimbirr identity.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-8 text-slate-300">
              Register once, verify your email, then carry the same trusted profile across Taban onboarding, Shimbirr notifications, software approvals, and secure access flows.
            </p>

            <div className="mt-8 grid gap-3">
              {[
                'One identity for the Shimbirr mobile app, web account, and connected Taban products.',
                'Verification code recovery stays inside this page if signup was started earlier.',
                'The same account powers notifications, social messaging, and product approvals.',
              ].map((item) => (
                <div key={item} className="rounded-full border border-[#ffcc00]/10 bg-black/30 px-4 py-3 text-sm text-slate-200">
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[2.4rem] border border-[#ffcc00]/12 bg-[#1c1b1b]/96 p-8 shadow-[0_24px_90px_rgba(0,0,0,0.32)] backdrop-blur-xl lg:p-10">
            <h2 className="text-3xl font-black tracking-[-0.03em] text-white">
              {step === 'register' ? 'Register account' : 'Verify your email'}
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              {step === 'register'
                ? 'Fill in your details to receive a one-time verification code.'
                : `Enter the code sent to ${pendingEmail}.`}
            </p>

            {step === 'register' ? (
              <form onSubmit={handleRegister} className="mt-8 grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="First name" icon={FiUser}>
                    <input
                      value={form.firstName}
                      onChange={updateField('firstName')}
                      placeholder="First name"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Last name" icon={FiUser}>
                    <input
                      value={form.lastName}
                      onChange={updateField('lastName')}
                      placeholder="Last name"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <Field label="Email" icon={FiMail}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={updateField('email')}
                    placeholder="name@company.com"
                    className={inputClass}
                  />
                </Field>

                <Field label="Password" icon={FiLock}>
                  <input
                    type="password"
                    value={form.password}
                    onChange={updateField('password')}
                    placeholder="Create a password"
                    className={inputClass}
                  />
                </Field>

                <Feedback error={error} message={message} />

                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-[#ffcc00] py-3 text-lg font-semibold text-[#131313] shadow-[0_18px_50px_rgba(255,204,0,0.24)] transition hover:-translate-y-0.5 disabled:opacity-70"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="mt-8 grid gap-5">
                <div className="rounded-[1.8rem] border border-[#ffcc00]/20 bg-[linear-gradient(135deg,rgba(255,204,0,0.18),rgba(255,255,255,0.04))] p-5 text-white shadow-[0_18px_45px_rgba(0,0,0,0.18)]">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-white/15 bg-black/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-100">
                      Step 2 of 2
                    </span>
                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-100">
                      Inbox: {maskedEmail}
                    </span>
                  </div>
                  <p className="mt-4 max-w-xl text-sm leading-7 text-slate-100/85">
                    Enter the newest six-digit verification code to activate the account and continue straight into the identity flow.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <VerifyHint icon={FiShield} text="Fresh codes replace old ones" />
                    <VerifyHint icon={FiCheckCircle} text="Verification unlocks onboarding" />
                    <VerifyHint icon={FiRefreshCw} text="Resend is built into this screen" />
                  </div>
                </div>

                <Field label="Verification code" icon={FiKey}>
                  <input
                    value={otp}
                    onChange={(event) => setOtp(event.target.value)}
                    placeholder="Enter 6-digit code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className={`${inputClass} text-lg font-semibold tracking-[0.35em]`}
                  />
                </Field>

                <Feedback error={error} message={message} />

                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300 py-3 text-lg font-semibold text-slate-950 shadow-[0_18px_50px_rgba(245,178,33,0.24)] transition hover:-translate-y-0.5 disabled:opacity-70"
                >
                  {loading ? 'Verifying...' : 'Verify and continue'}
                </button>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-70"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('register');
                      setOtp('');
                      setError('');
                      setMessage('');
                    }}
                    className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-slate-300 transition hover:bg-white/5"
                  >
                    Edit details
                  </button>
                </div>
              </form>
            )}

            <p className="mt-6 text-center text-sm text-slate-300">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-bold text-amber-300 transition hover:text-amber-200"
              >
                Sign in
              </button>
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-300">{label}</span>
      <div className="flex items-center rounded-full border border-amber-300/40 bg-white/5 px-5 py-3 text-white">
        <Icon className="mr-3 h-5 w-5 shrink-0 text-amber-300" />
        {children}
      </div>
    </label>
  );
}

function VerifyHint({ icon: Icon, text }) {
  return (
    <div className="rounded-[1.3rem] border border-white/10 bg-black/20 px-4 py-3 text-sm text-slate-100/85">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-amber-200" />
        <span>{text}</span>
      </div>
    </div>
  );
}

function Feedback({ error, message }) {
  if (error) {
    return (
      <div className="rounded-[1.4rem] border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
        {error}
      </div>
    );
  }

  if (message) {
    return (
      <div className="rounded-[1.4rem] border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
        {message}
      </div>
    );
  }

  return null;
}

const inputClass = 'w-full bg-transparent text-white placeholder-slate-500 outline-none';
